// Scoring functions powering the grass-comparison tool verdict.
//
// We turn each selected grass into a 0–5 score on six traits, declare a winner
// per trait, sum the wins, and optionally add a 0–2 climate-zone bonus when
// the user has entered a ZIP. The functions are pure and synchronous so the
// verdict + table can derive from props every render.

import { GrassCharacteristics } from './types/recommendation-engine'
import { toleranceToNumber } from './grass-comparison-helpers'
import { getGrassComparisonUi } from '../data/grass-comparison-ui'
import { REGIONAL_CLIMATE_DATA, getRegionFromZipCode } from '../data/regional-climate'
import {
  attachToolEngineMetadata,
  buildCanonicalToolContext,
  type DeterministicToolResultMetadata,
} from '@/lib/lawn-plan-engine'

export type TraitId = 'drought' | 'shade' | 'cold' | 'traffic' | 'maint' | 'cost'

export interface TraitDef {
  id: TraitId
  label: string
  blurb: string
}

export const GRASS_COMPARISON_TRAITS: TraitDef[] = [
  { id: 'drought', label: 'Drought tolerance', blurb: 'Survives weeks without rain' },
  { id: 'shade', label: 'Shade tolerance', blurb: 'OK under trees, north walls' },
  { id: 'cold', label: 'Cold tolerance', blurb: 'Stays green into late fall' },
  { id: 'traffic', label: 'Foot traffic', blurb: 'Kids, pets, parties' },
  { id: 'maint', label: 'Maintenance', blurb: 'Mowing, watering, feeding' },
  { id: 'cost', label: 'Cost to install', blurb: 'Seed or sod, per 1000 sqft' },
]

// Map the free-form `coldTolerance` string ("Very High (dormant below 50°F)",
// "Low (dies under 25°F)", etc.) to a 0–5 score. Falls back to 3 when the
// value is missing or unrecognised so we never produce NaN.
export function coldToleranceToNumber(value: string | undefined): number {
  if (!value) return 3
  const lower = value.toLowerCase()
  if (lower.includes('very low')) return 1
  if (lower.includes('very high')) return 5
  if (lower.includes('medium-high') || lower.includes('moderate-high')) return 4
  if (lower.includes('medium-low') || lower.includes('moderate-low')) return 2
  if (lower.includes('low')) return 2
  if (lower.includes('high')) return 4
  if (lower.includes('medium') || lower.includes('moderate')) return 3
  return 3
}

// Invert maintenance: design semantics are "higher = easier to live with".
const MAINTENANCE_TO_SCORE: Record<string, number> = {
  'Very Low': 5,
  Low: 4,
  Medium: 3,
  'Medium-High': 2,
  High: 1,
}

export function maintenanceLevelToScore(value: string | undefined): number {
  if (!value) return 3
  return MAINTENANCE_TO_SCORE[value] ?? 3
}

// Compute the 0–5 score for a single trait. Pulls from GrassCharacteristics
// for five traits and from the editorial UI metadata for `cost`.
export function getTraitScore(grass: GrassCharacteristics, trait: TraitId): number {
  switch (trait) {
    case 'drought':
      return toleranceToNumber(grass.droughtTolerance)
    case 'shade':
      return toleranceToNumber(grass.shadeTolerance)
    case 'cold':
      return coldToleranceToNumber(grass.coldTolerance)
    case 'traffic':
      return toleranceToNumber(grass.trafficTolerance)
    case 'maint':
      return maintenanceLevelToScore(grass.maintenanceLevel)
    case 'cost':
      return getGrassComparisonUi(grass.name).costToInstall
    default:
      return 3
  }
}

// Return the index of the highest-scoring grass on a trait, or -1 if two or
// more grasses tie for first place.
export function pickWinnerIndex(grasses: GrassCharacteristics[], trait: TraitId): number {
  if (grasses.length === 0) return -1
  let bestScore = -1
  let bestIdx = -1
  let tied = false
  grasses.forEach((g, i) => {
    const score = getTraitScore(g, trait)
    if (score > bestScore) {
      bestScore = score
      bestIdx = i
      tied = false
    } else if (score === bestScore) {
      tied = true
    }
  })
  return tied ? -1 : bestIdx
}

// Zone-fit bonus, 0–2. Falls out of `REGIONAL_CLIMATE_DATA` so we don't need a
// USDA-zone table:
//   2 → grass is in the region's `primaryGrasses` list
//   1 → grass matches the region's prevailing season (warm region + warm grass,
//       or cool region + cool grass) but isn't explicitly listed
//   0 → otherwise
export function computeZoneBonus(
  grass: GrassCharacteristics,
  region: string | null,
): 0 | 1 | 2 {
  if (!region || region === 'Unknown') return 0
  const climate = REGIONAL_CLIMATE_DATA[region]
  if (!climate) return 0
  if (climate.primaryGrasses.includes(grass.name)) return 2

  const regionIsWarm = climate.zone.toLowerCase().includes('warm')
  const regionIsCool = climate.zone.toLowerCase().includes('cool')
  if (regionIsWarm && grass.season === 'Warm') return 1
  if (regionIsCool && grass.season === 'Cool') return 1
  return 0
}

export type ClimateSeason = 'Warm' | 'Cool'

/**
 * Returns the region's explicit season when it has one. Transition and mixed
 * regions intentionally return null because both warm- and cool-season grasses
 * can be reasonable there.
 */
export function getRegionClimateSeason(region: string | null): ClimateSeason | null {
  if (!region || region === 'Unknown') return null
  const zone = REGIONAL_CLIMATE_DATA[region]?.zone.toLowerCase()
  if (!zone || zone.includes('mixed') || zone.includes('transition')) return null
  if (zone.includes('warm')) return 'Warm'
  if (zone.includes('cool')) return 'Cool'
  return null
}

/** A mismatched grass cannot become the recommendation just by winning traits. */
export function isClimateCompatible(
  grass: GrassCharacteristics,
  region: string | null,
): boolean {
  if (!region || region === 'Unknown') return true
  const climate = REGIONAL_CLIMATE_DATA[region]
  if (!climate) return true
  if (climate.primaryGrasses.includes(grass.name)) return true
  const climateSeason = getRegionClimateSeason(region)
  return climateSeason === null || grass.season === climateSeason
}

export interface ComputedTotals extends DeterministicToolResultMetadata {
  /** Per-grass trait wins. */
  wins: number[]
  /** Per-grass zone-fit bonus, 0–2. */
  zoneBonuses: Array<0 | 1 | 2>
  /** Wins + zone bonus. */
  totals: number[]
  /** Max possible score for the chip denominator. */
  maxScore: number
  /** Index of the winning grass, or -1 if tied. */
  winnerIdx: number
  tied: boolean
  /** Whether each grass is viable for the ZIP's prevailing climate. */
  climateCompatible: boolean[]
  /** Explicit warm/cool season for the region, or null for mixed/transition. */
  climateSeason: ClimateSeason | null
  /** True when every compared grass conflicts with an explicit climate season. */
  noClimateCompatibleWinner: boolean
  /** Source-backed warnings when the selected site conflicts with grass tolerance. */
  siteFitWarnings?: string[]
}

export function computeTotals(
  grasses: GrassCharacteristics[],
  zip: string | null,
  options: { sunlight?: string | null } = {},
): ComputedTotals {
  const context = buildCanonicalToolContext({
    toolId: 'grass_comparison',
    grassType: grasses[0]?.name ?? null,
    sunlight: options.sunlight ?? null,
  })
  const region = zip ? getRegionFromZipCode(zip) : null
  const wins = grasses.map(() => 0)
  GRASS_COMPARISON_TRAITS.forEach((t) => {
    const w = pickWinnerIndex(grasses, t.id)
    if (w >= 0) wins[w] += 1
  })

  const zoneBonuses = grasses.map((g) => computeZoneBonus(g, region)) as Array<0 | 1 | 2>
  const totals = wins.map((w, i) => w + zoneBonuses[i])
  const climateCompatible = grasses.map((grass) => isClimateCompatible(grass, region))
  const climateSeason = getRegionClimateSeason(region)
  const eligibleIndexes = climateCompatible
    .map((compatible, index) => (compatible ? index : -1))
    .filter((index) => index >= 0)
  const noClimateCompatibleWinner = grasses.length > 0 && eligibleIndexes.length === 0

  let winnerIdx = eligibleIndexes[0] ?? -1
  let tied = false
  for (const i of eligibleIndexes.slice(1)) {
    if (winnerIdx < 0 || totals[i] > totals[winnerIdx]) {
      winnerIdx = i
      tied = false
    } else if (totals[i] === totals[winnerIdx]) {
      tied = true
    }
  }
  const maxScore = GRASS_COMPARISON_TRAITS.length + (region && region !== 'Unknown' ? 2 : 0)
  const sunlightWarnings = grasses.flatMap((grass) => {
    const grassContext = buildCanonicalToolContext({
      toolId: 'grass_comparison',
      grassType: grass.name,
      sunlight: options.sunlight ?? null,
    })
    return grassContext.metadata.inputEffects
      .filter((effect) => effect.input === 'sunlight' && effect.effect.includes('low shade tolerance'))
      .map((effect) => effect.effect)
  })
  const climateWarnings = grasses.flatMap((grass, index) => {
    if (climateCompatible[index] || !climateSeason || !region) return []
    return [
      `${grass.name} is a ${grass.season.toLowerCase()}-season grass and is not a fit for ${region}'s ${climateSeason.toLowerCase()}-season climate.`,
    ]
  })

  return attachToolEngineMetadata(
    {
      wins,
      zoneBonuses,
      totals,
      maxScore,
      winnerIdx,
      tied,
      climateCompatible,
      climateSeason,
      noClimateCompatibleWinner,
      siteFitWarnings: [...new Set([...sunlightWarnings, ...climateWarnings])],
    },
    context,
  )
}
